/**
 * Client-side mirror of the backend price crash engine.
 * Must stay numerically in sync with `backend/core/live_engine.py`:
 *   - PRICE_DROP_PERCENTS ...................................... same as DROP_PERCENTS
 *   - MILESTONE_AUDIENCE_PERCENTS (10/20/35/55/80) ............. cohort × these → thresholds
 *   - compute_milestone_prices(starting, floor) ............... = computeMilestonePrices(...)
 *   - price_for_viewers(...) .................................. = priceForViewers(...)
 *
 * Thresholds are fetched at runtime from GET /api/price-engine/cohort (they
 * auto-scale with the active cohort: Founding 250 → Early Insiders 1000 → …).
 * `FALLBACK_VIEWER_MILESTONES` mirrors the Founding cohort so UIs are usable
 * before the cohort call resolves.
 */

export const DROP_PERCENTS = [0.05, 0.10, 0.15, 0.30, 0.40];
export const FALLBACK_VIEWER_MILESTONES = [25, 50, 88, 138, 200];

/**
 * Compute the 5 milestone prices from starting → floor using DROP_PERCENTS.
 * Returns a flat array of 5 numbers (rounded to 2 decimals, last snapped to floor).
 */
export function computeMilestonePrices(starting, floor) {
  const s = Number(starting) || 0;
  const f = Number(floor) || 0;
  const diff = s - f;
  if (diff <= 0) return Array(5).fill(Math.round(s * 100) / 100);
  const prices = [];
  let p = s;
  for (const pct of DROP_PERCENTS) {
    p -= pct * diff;
    prices.push(Math.round(p * 100) / 100);
  }
  prices[4] = Math.round(f * 100) / 100; // strictly snap to floor
  return prices;
}

/**
 * Zip the 5 prices with the given viewer thresholds to produce the
 * [{threshold, price}] shape the backend + MilestoneStepper expect.
 */
export function computeMilestones(starting, floor, thresholds = FALLBACK_VIEWER_MILESTONES) {
  const prices = computeMilestonePrices(starting, floor);
  return thresholds.map((t, i) => ({ threshold: t, price: prices[i] }));
}

/**
 * Given current viewer count, return the milestone price the crash engine
 * would surface (walks thresholds high → low). Falls back to `starting`
 * before the first threshold is unlocked.
 */
export function priceForViewers(viewers, starting, milestonePrices, thresholds) {
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (viewers >= thresholds[i]) return milestonePrices[i];
  }
  return starting;
}
